import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Phone, Mail, MapPin, Clock } from "lucide-react";
import MaroonNavbar from "@/components/new/MaroonNavbar";
import MaroonFooter from "@/components/new/MaroonFooter";
import CheckoutDrawer from "@/components/new/CheckoutDrawer";
import WhatsAppButton from "@/components/new/WhatsAppButton";
import HoverCycleImage from "@/components/new/HoverCycleImage";

const C = { maroon: "#621B21", maroonDark: "#5C0F1C", yellow: "#F5C800", beige: "#F5EDD6", white: "#FFFFFF" };
const LOGO = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/xwzvhvs5_Header%20Logo%20Ethia.png";
const HERO_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/x1jlom55_Hero%20Image.png";
const EVERY_POUCH_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/nqq4m12m_Every%20Pouch%20Every%20Promise%20Image.png";
const CHICKEN_SAMPLE_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/owqmqjik_chicken%20sample.png";
const TRIAL_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/pc8zitpq_1.png";
const VALUE_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/hidm2s7t_2.png";
const FAMILY_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/d68votma_3.png";
const BACK_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/kuzbrmjm_Ethia%20Back%20of%20the%20Pack%20Image.webp";
const TRIAL_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/cmpv1zf0_Ethia%20Trial%20Pack%20Image%203.png";
const VALUE_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/g0tiuobp_Ethia%20Value%20Pack%20Image%202.png";
const FAMILY_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/ooeh3ljq_Ethia%20Family%20Pack%203.png";
const TRIAL_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/whgfkde7_Trial%20Pack%20Ethia%20%281%29.png";
const VALUE_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/lp2gvf2w_Ethia%20Value%20Pack%20%281%29.png";
const FAMILY_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/qcz1ezz2_Ethia%20Family%20Pack%20%281%29.png";
const LARGE_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/4ed9b9118f0f671d_Large%20Pack%201%20pouch%20of%20300g%20Price.png";
const LAB_IMG = "https://images.pexels.com/photos/9574465/pexels-photo-9574465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DOG_FOOD_IMG = "https://images.pexels.com/photos/6568942/pexels-photo-6568942.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/* Hook for scroll reveal */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

/* ─── SECTION 1: HERO ─── */
const HeroSection = ({ onOrder }) => (
  <section className="relative pt-24 md:pt-28 overflow-hidden" style={{ background: C.maroonDark }} data-testid="hero-section">
    <div className="max-w-[1380px] mx-auto px-5 md:px-[52px] py-10 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left */}
        <div className="text-center lg:text-left">
          <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
            <span className="px-4 py-1.5 rounded-full text-xs font-bold" style={{ background: C.yellow, color: C.maroon }}>First Time in India</span>
            <span className="px-4 py-1.5 rounded-full text-xs font-bold" style={{ background: C.yellow, color: C.maroon }}>100% Slaughter Free</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.1] mb-6" style={{ color: C.white, fontFamily: "Fraunces, serif" }} data-testid="hero-headline">
            No Harm.<br />No Compromise.<br /><span style={{ color: C.yellow }}>Just Real Food.</span>
          </h1>
          <p className="text-sm md:text-base mb-4" style={{ color: C.beige, fontFamily: "Manrope, sans-serif" }}>Safe. Clean. Scientifically made. Loved by dogs.</p>
          <div className="rounded-xl px-5 py-3 mb-6 inline-block" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <p className="text-sm" style={{ color: `${C.beige}CC`, fontFamily: "Manrope, sans-serif" }}>The first dog food made from real chicken — without harming a single animal.</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-6">
            <button onClick={onOrder} className="font-bold px-8 py-4 rounded-full text-sm transition-all hover:scale-105 shadow-lg" style={{ background: C.yellow, color: C.maroon }} data-testid="hero-cta-try">Try ETHIA <ArrowRight className="inline ml-1" size={16} /></button>
            <a href="/science" className="font-bold px-8 py-4 rounded-full text-sm transition-all hover:scale-105" style={{ border: `2px solid ${C.white}`, color: C.white }} data-testid="hero-cta-learn">Learn More</a>
          </div>
          <p className="text-xs italic" style={{ color: `${C.beige}99`, fontFamily: "Manrope, sans-serif" }}>A small bowl of food with a very big heart behind it.</p>
        </div>
        {/* Right — Static hero image, no animation */}
        <div className="flex justify-center lg:justify-end">
          <a href="/products" className="block w-full" data-testid="hero-img-link">
            <img src={HERO_IMG} alt="ETHIA Products" className="w-full rounded-3xl shadow-2xl" data-testid="hero-img" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* ─── SECTION 2: SCROLLING TRUST BAR ─── */
const TrustBar = () => {
  const items = ["Clean Label Guarantee", "Made in India", "100% Slaughter Free", "No Artificial Preservatives", "Real Chicken Cells", "Planet Friendly", "Vet Approved"];
  return (
    <section className="py-4 overflow-hidden" style={{ background: C.beige }} data-testid="trust-bar">
      <div className="flex animate-drift whitespace-nowrap">
        {[...items, ...items, ...items].map((item, i) => (
          <span key={i} className="inline-flex items-center gap-2 mx-6 text-sm font-semibold" style={{ color: C.maroon, fontFamily: "Manrope, sans-serif" }}>
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ background: C.yellow, color: C.white }}>&#10003;</span>
            {item}
          </span>
        ))}
      </div>
    </section>
  );
};

/* ─── SECTION 3: IS IT JUSTIFIABLE ─── */
const JustifiableSection = () => {
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-16 md:py-24" style={{ background: C.beige }} data-testid="justifiable-section">
      <div className={`max-w-[700px] mx-auto px-6 text-center transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <Heart size={32} className="mx-auto mb-4" style={{ color: "#C0392B" }} fill="#C0392B" />
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold italic leading-tight mb-6" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>
          "Is it justifiable to harm one life to nourish another?"
        </h2>
        <p className="text-sm md:text-base leading-relaxed mb-8" style={{ color: `${C.maroon}B3`, fontFamily: "Manrope, sans-serif" }}>
          For decades, being a dog lover meant accepting a difficult compromise. We believe your pet's health should not come at the cost of another life. We have pioneered a way to grow real chicken protein that is kinder to animals and safer for your adorable pet.
        </p>
        <a href="/about" className="inline-block font-bold px-8 py-3.5 rounded-full text-sm transition-all hover:scale-105" style={{ border: `2px solid ${C.maroon}`, color: C.maroon, fontFamily: "Manrope, sans-serif" }} data-testid="justifiable-cta">What do we mean by cultivated? <ArrowRight className="inline ml-1" size={16} /></a>
      </div>
    </section>
  );
};

const PRODUCT_SCREENSHOT = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/dlfcp1ed_Screenshot%202026-05-12%20at%205.47.09%E2%80%AFPM.png";
const BOWL_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/dv899zqk_bowl%20image.png";

/* ─── SECTION 3B: CLAIM BADGES ─── */
const ClaimBadgesSection = () => {
  const badges = [
    { icon: "🌿", label: "Non-GMO" },
    { icon: "🇮🇳", label: "Made in India" },
    { icon: "⭐", label: "Premium Quality" },
    { icon: "🚫", label: "No Artificial Flavours" },
    { icon: "✅", label: "Easy to Digest" },
    { icon: "👩\u200D⚕️", label: "Vet Approved" },
  ];
  return (
    <section className="py-12 md:py-16" style={{ background: C.beige }} data-testid="claim-badges-section">
      <div className="max-w-[1100px] mx-auto px-5 md:px-[52px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="grid grid-cols-2 gap-3">
            {badges.map((b, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: `${C.beige}`, border: `1px solid ${C.maroon}15` }} data-testid={`claim-badge-${i}`}>
                <span className="text-lg flex-shrink-0" style={{ color: C.yellow }}>{b.icon}</span>
                <span className="text-sm font-semibold" style={{ color: C.maroon, fontFamily: "Manrope, sans-serif" }}>{b.label}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <img src={PRODUCT_SCREENSHOT} alt="ETHIA Product" className="w-full max-w-md rounded-2xl" style={{ boxShadow: "0 8px 32px rgba(98,27,33,0.1)" }} />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── SECTION 4: EVERY POUCH EVERY PROMISE ─── */
const EveryPouchSection = () => {
  const [hovered, setHovered] = useState(false);
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-16 md:py-24" style={{ background: C.maroon }} data-testid="every-pouch-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-[52px]">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex justify-center">
            <img src={EVERY_POUCH_IMG} alt="Every Pouch Every Promise" className="w-full max-w-md rounded-2xl shadow-2xl object-cover" />
          </div>
          <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 cursor-default" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>
              Every Pouch.<br />Every Promise. &#9670;
            </h2>
            <div className={`transition-all duration-500 overflow-hidden ${hovered ? "max-h-40 opacity-100" : "max-h-0 opacity-0 md:max-h-40 md:opacity-100"}`}>
              <p className="text-sm md:text-base leading-relaxed mb-6" style={{ color: `${C.beige}CC`, fontFamily: "Manrope, sans-serif" }}>
                We did not change how your dog eats. We changed how the meat is grown. 100% real protein. No slaughter.
              </p>
              <a href="/products" className="inline-block font-bold px-8 py-3.5 rounded-full text-sm transition-all hover:scale-105" style={{ background: C.yellow, color: C.maroon, fontFamily: "Manrope, sans-serif" }} data-testid="pouch-cta">View Products <ArrowRight className="inline ml-1" size={16} /></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── SECTION 5: OUR SCIENCE (scroll reveal) ─── */
const ScienceSteps = () => {
  const steps = [
    { num: "01", title: "Chicken Sample", img: CHICKEN_SAMPLE_IMG, desc: "We culture real chicken cells that were extracted from the egg in a one-time process. These cells are grown in a clean and sterile environment, where they naturally multiply. No antibiotics." },
    { num: "02", title: "Cells Grown Safely", img: LAB_IMG, desc: "Once we achieve the required density, these cells are harvested. We don't need any animals in our whole lifetime now." },
    { num: "03", title: "Pure Protein Food", img: DOG_FOOD_IMG, desc: "These cells are blended with clean label food ingredients to create a nutritious, balanced pet food designed for your adorable pet. It's packaged and retorted. Now it's ready to be served." },
  ];

  return (
    <section className="py-16 md:py-24" style={{ background: C.beige }} data-testid="science-steps-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
        <div className="text-center mb-4">
          <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.maroon }}>OUR SCIENCE</span>
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-14" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>How We Make It</h2>
        <div className="space-y-0">
          {steps.map((step, i) => (
            <ScienceStep key={i} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ScienceStep = ({ step, index }) => {
  const [ref, vis] = useReveal();
  const [hover, setHover] = useState(false);
  return (
    <div ref={ref} className={`sticky top-24 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ zIndex: index + 1 }}>
      <div className="rounded-2xl p-6 md:p-10 mb-6 shadow-lg" style={{ background: C.white }}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr_1.5fr] gap-6 items-center">
          <div>
            <span className="text-5xl md:text-6xl font-black" style={{ color: `${C.maroon}20`, fontFamily: "Fraunces, serif" }}>{step.num}</span>
            <h3 className="text-lg md:text-xl font-bold mt-1" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>{step.title}</h3>
          </div>
          <div className="flex justify-center" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            <div className="relative">
              <img src={step.img} alt={step.title} className="w-full max-w-[220px] h-40 object-cover rounded-xl shadow-md transition-transform duration-300 hover:scale-105" />
              {hover && <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center p-3 transition-opacity"><p className="text-white text-xs text-center" style={{ fontFamily: "Manrope, sans-serif" }}>{step.desc.slice(0, 80)}...</p></div>}
            </div>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: `${C.maroon}99`, fontFamily: "Manrope, sans-serif" }}>{step.desc}</p>
        </div>
      </div>
    </div>
  );
};

/* ─── SECTION 6: SOCIAL PROOF ─── */
const TestimonialsSection = () => {
  const testimonials = [
    { name: "Arun", quote: "My pet dog absolutely loved it. We wanna buy more..." },
    { name: "Atharva", quote: "Chai absolutely loved the wet food. He really seemed to enjoy both the texture and the taste and finished it very enthusiastically." },
    { name: "Ujvala", quote: "My pet dog had one and loved it!" },
    { name: "Aaditya Rudra, Team Jimmy, Gypsy & Piku", quote: "We tried the food with all three of our dogs\u2014Jimmy, Gypsy, and Piku\u2014and it was awesome! They absolutely loved it and enjoyed every bit of it. We're really happy with the quality and taste, and it was great to see them enjoy it so much." },
    { name: "Anjali Chauhan", quote: "Whiskey liked ur food very much...it's 10/10. He just finished it all.." },
    { name: "Shalu Singh", quote: "Thank you for sending this for Mowgli. Usko genuinely pasand aaya. Yeah, he was a bit unsure at first \u2014 just sniffed it, licked a little, and walked away. But after a while, he came back and ate it." },
  ];
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-16 md:py-24 overflow-hidden" style={{ background: C.beige }} data-testid="testimonials-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>Loved by Dog Parents.</h2>
        <p className="text-center text-sm md:text-base mb-12" style={{ color: `${C.maroon}99`, fontFamily: "Manrope, sans-serif" }}>Trusted by early adopters and pet nutrition experts.</p>
      </div>
      <div className={`transition-opacity duration-700 ${vis ? "opacity-100" : "opacity-0"}`}>
        <div className="flex animate-drift-slow whitespace-nowrap gap-6 px-6">
          {[...testimonials, ...testimonials].map((t, i) => (
            <div key={i} className="inline-block whitespace-normal w-[300px] md:w-[360px] flex-shrink-0 rounded-2xl p-6 shadow-md" style={{ background: C.white }} data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 mb-3">{Array(5).fill(0).map((_, s) => <span key={s} style={{ color: C.yellow }} className="text-lg">&#9733;</span>)}</div>
              <p className="text-sm leading-relaxed mb-4 italic" style={{ color: `${C.maroon}CC`, fontFamily: "Manrope, sans-serif" }}>"{t.quote}"</p>
              <p className="font-bold text-sm" style={{ color: C.maroon, fontFamily: "Manrope, sans-serif" }}>&mdash; {t.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── INSTAGRAM REELS SECTION ─── */
const INSTA_VIDEOS = [
  "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/lb76wpte_1.mp4",
  "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/op3qc1p0_2.mp4",
  "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/3wvh6l4e_3.mp4",
  "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/j9wtgwcz_4.mp4",
  "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/bjguv6pa_5.MOV",
];

const InstagramReelsSection = () => (
  <section className="py-10 md:py-14 overflow-hidden" style={{ background: C.maroon }} data-testid="instagram-reels">
    <div className="marquee-reels">
      <div className="marquee-reels-track">
        {[...INSTA_VIDEOS, ...INSTA_VIDEOS].map((vid, i) => (
          <a key={i} href="https://www.instagram.com/ethiapet" target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-[180px] md:w-[220px] rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300" style={{ aspectRatio: "9/16" }} data-testid={`insta-reel-${i}`}>
            <video src={vid} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
    <style>{`
      .marquee-reels { overflow: hidden; }
      .marquee-reels-track {
        display: flex;
        gap: 1rem;
        animation: marqueeReels 80s linear infinite;
        width: max-content;
      }
      @keyframes marqueeReels {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
    `}</style>
  </section>
);

/* ─── SECTION 7: CHOOSE YOUR PACK (matches Products page style) ─── */
const ProductsPreview = ({ onOrder }) => {
  const products = [
    { id: "trial", slug: "trial-pack", name: "Trial Pack", desc: "2 Pouches of 80g each · 160g in total", price: 199, gstPrice: 235, images: [TRIAL_NEW, BACK_IMG, TRIAL_ACTION] },
    { id: "value", slug: "value-pack", name: "Value Pack", desc: "5 Pouches of 80g each · 400g in total", price: 499, gstPrice: 589, images: [VALUE_NEW, BACK_IMG, VALUE_ACTION] },
    { id: "large", slug: "large-pack", name: "Large Pack", desc: "1 Pouch of 300g", price: 237, gstPrice: 280, images: [LARGE_NEW, BACK_IMG, LARGE_NEW] },
    { id: "family", slug: "family-pack", name: "Family Pack", desc: "12 Pouches of 80g each · 960g in total", price: 999, gstPrice: 1179, images: [FAMILY_NEW, BACK_IMG, FAMILY_ACTION], popular: true },
  ];
  return (
    <section className="py-16 md:py-24" style={{ background: "#4a0e0e" }} data-testid="products-preview">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>ORDER NOW</span></div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Choose Your Pack</h2>
        <p className="text-center text-sm md:text-base mb-12" style={{ color: `${C.beige}CC`, fontFamily: "Manrope, sans-serif" }}>Be among the first to experience cultivated pet food in India</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {products.map(p => (
            <div key={p.id} className={`rounded-2xl overflow-hidden shadow-xl transition-all duration-300 hover:-translate-y-2 ${p.popular ? "ring-2" : ""}`} style={{ background: "#f2ede7", ringColor: p.popular ? "#e6b93d" : "transparent" }} data-testid={`home-product-${p.id}`}>
              {p.popular && <div className="text-center py-2.5 text-sm font-bold" style={{ background: "#e6b93d", color: "#4a0e0e" }}>Most Popular</div>}
              <div className="p-3">
                <div className="rounded-xl overflow-hidden" style={{ aspectRatio: "4/5", background: "#f8f5f0" }}>
                  <HoverCycleImage images={p.images} slug={p.slug} alt={p.name} className="w-full h-full object-contain rounded-xl" />
                </div>
              </div>
              <div className="px-5 pb-5 text-center">
                <Link to={`/products/${p.slug}`} className="hover:underline">
                  <h3 className="text-lg md:text-xl font-bold mb-1" style={{ color: "#4a0e0e", fontFamily: "Fraunces, serif" }}>{p.name}</h3>
                </Link>
                <p className="text-xs mb-4" style={{ color: "#4a0e0e", opacity: 0.7 }}>{p.desc}</p>
                <div className="mb-4">
                  <div className="text-3xl font-black" style={{ color: "#4a0e0e", fontFamily: "Fraunces, serif" }}>₹{p.price}</div>
                  <div className="text-xs mt-1" style={{ color: "#4a0e0e", opacity: 0.5 }}>₹{p.gstPrice.toLocaleString()} (incl. GST)</div>
                </div>
                <button onClick={() => onOrder(p.id)} className="w-full font-bold py-3.5 rounded-full text-sm transition-all hover:scale-[1.02]" style={{ background: "#e6b93d", color: "#4a0e0e" }} data-testid={`home-order-${p.id}`}>
                  Order Now <ArrowRight className="inline ml-1" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── SECTION 8: CONTACT INFO CARDS ─── */
const ContactInfoCards = () => {
  const cards = [
    { icon: <Phone size={24} />, label: "Phone", value: "+91 93279 27782", sub: "Mon-Sat 9AM-6PM" },
    { icon: <Mail size={24} />, label: "Email", value: "admin@ethia.in", sub: "24/7 Support" },
    { icon: <MapPin size={24} />, label: "Location", value: "Mumbai, Maharashtra", sub: "India" },
    { icon: <Clock size={24} />, label: "Hours", value: "Monday to Saturday", sub: "9:00 AM to 6:00 PM" },
  ];
  return (
    <section className="py-16 md:py-24" style={{ background: C.beige }} data-testid="contact-info-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.maroon }}>GET IN TOUCH</span></div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>We'd Love to Hear From You</h2>
        <p className="text-center text-sm md:text-base mb-12" style={{ color: `${C.maroon}99` }}>Have questions about ETHIA? Our team is here to help.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {cards.map((c, i) => (
            <div key={i} className="rounded-2xl p-5 md:p-6 text-center hover:-translate-y-1 transition-all duration-300" style={{ background: C.maroon }} data-testid={`info-card-${i}`}>
              <div className="flex justify-center mb-3" style={{ color: C.yellow }}>{c.icon}</div>
              <div className="font-bold text-xs md:text-sm mb-1" style={{ color: C.yellow }}>{c.label}</div>
              <div className="font-semibold text-xs md:text-sm mb-1 break-words" style={{ color: C.white }}>{c.value}</div>
              <div className="text-[10px] md:text-xs" style={{ color: `${C.white}99` }}>{c.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── SECTION 9: FINAL CTA ─── */
const FinalCTA = ({ onOrder }) => (
  <section className="py-16 md:py-20" style={{ background: C.beige }} data-testid="final-cta">
    <div className="max-w-[800px] mx-auto px-6">
      <div className="rounded-2xl md:rounded-3xl p-8 md:p-14 text-center" style={{ background: C.maroonDark }}>
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Be First. Feed Better. Save Lives. &#128062;</h3>
        <p className="text-sm md:text-base mb-8" style={{ color: `${C.beige}CC`, fontFamily: "Manrope, sans-serif" }}>Be the first in India to feed your dog food that saves lives.</p>
        <button onClick={onOrder} className="font-bold px-10 py-4 rounded-full transition-all hover:scale-105 text-sm md:text-base" style={{ background: C.yellow, color: C.maroon }} data-testid="final-cta-btn">Order Now <ArrowRight className="inline ml-1" size={18} /></button>
      </div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const HomePage = () => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);

  const handleOrder = (sku) => {
    setSelectedPkg(sku || null);
    setIsCheckoutOpen(true);
    if (typeof window.fbq === "function") window.fbq("track", "InitiateCheckout");
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="home-page">
      <MaroonNavbar onOrder={() => handleOrder(null)} />
      <HeroSection onOrder={() => handleOrder(null)} />
      <TrustBar />
      <JustifiableSection />
      <ClaimBadgesSection />
      <EveryPouchSection />
      <ScienceSteps />
      <TestimonialsSection />
      <InstagramReelsSection />
      <ProductsPreview onOrder={handleOrder} />
      <ContactInfoCards />
      <FinalCTA onOrder={() => handleOrder(null)} />
      <MaroonFooter />
      <WhatsAppButton />
      <CheckoutDrawer isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} preselectedSku={selectedPkg} />
    </div>
  );
};

export default HomePage;
